import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { type Conversation } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';
import { meetingProjects, rememberTeamProject } from '../lib/team-projects.js';
import { useDrawer } from '../state/drawer.js';
import { useCompanion } from '../state/companion.js';
import { useSession } from '../state/session.js';
import { useMentions } from '../state/mentions.js';
import { EmbedThreadShell, useEmbed } from './Embed.js';
import { HealthBanner } from './HealthBanner.js';
import { JabberCompanion } from './JabberCompanion.js';
import { NoticeBanner } from './NoticeBanner.js';
import { MentionBanner } from './MentionBanner.js';
import { DetailsDrawer } from './layout/DetailsDrawer.js';
import { Sidebar } from './layout/Sidebar.js';
import { TopBar } from './layout/TopBar.js';
import { useProjectThreads } from './layout/useProjectThreads.js';
import { ScreenNavigation } from './ScreenNavigation.js';
import { MenuIcon } from './icons.js';

export function Layout() {
  const { projects } = useSession();
  const embed = useEmbed();
  const navigate = useNavigate();
  const projectMatch = useMatch('/p/:projectId/*');
  const { pathname } = useLocation();
  const projectId = projectMatch?.params.projectId;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drawer = useDrawer();
  const { user } = useSession();
  const { done } = useCompanion();

  const conversationMatch = useMatch('/p/:projectId/c/:conversationId');
  const openConversationId = conversationMatch?.params.conversationId;
  const { count: mentionCount } = useMentions(user?.id, openConversationId);

  // Mobile keyboards resize the visual viewport, including inside the native web view.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const resize = (): void => {
      if (viewport.scale === 1) document.documentElement.style.setProperty('--visible-viewport-height', `${viewport.height}px`);
    };
    resize();
    viewport.addEventListener('resize', resize);
    return () => {
      viewport.removeEventListener('resize', resize);
      document.documentElement.style.removeProperty('--visible-viewport-height');
    };
  }, []);

  // The details drawer opens only from the conversation header; leave a conversation and it closes.
  useEffect(() => {
    if (!conversationMatch && drawer.open) drawer.setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationMatch]);

  // Unread replies show in the tab title while Jabberr is in a background tab; unread
  // mentions win when both exist.
  useEffect(() => {
    const update = (): void => {
      if (mentionCount > 0) document.title = `(${mentionCount}) Jabberr`;
      else if (done.length > 0 && document.hidden) document.title = `(${done.length}) Jabberr`;
      else document.title = 'Jabberr';
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [done, mentionCount]);

  const threadState = useProjectThreads({ projectId, openConversationId, pathname, drawerInfo: drawer.info });

  const { ensureExpanded, prependThread } = threadState;

  const newConversation = useCallback(
    async (pid?: string) => {
      const target = pid ?? projectId;
      if (!target || creating) return;
      setCreating(true);
      try {
        // No role: the server picks the project's default (open in My space, product owner in a project).
        const { conversation } = await api.conversations.create(target, { reasoningEffort: 'low' });
        ensureExpanded(target);
        prependThread(target, conversation);
        setSidebarOpen(false);
        navigate(`/p/${target}/c/${conversation.id}`);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setCreating(false);
      }
    },
    [projectId, creating, navigate, ensureExpanded, prependThread],
  );

  // Cmd/Ctrl+K: new conversation in the current project.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!embed && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        void newConversation();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [newConversation, embed]);

  const currentProject = projects.find((p) => p.id === projectId);

  useEffect(() => {
    if (user && projectId && meetingProjects(projects).some(project => project.id === projectId)) rememberTeamProject(user.id, projectId);
  }, [user?.id, projectId, projects]);

  const onMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 800px)').matches;

  if (embed) return <EmbedThreadShell><HealthBanner /><Outlet context={{ projectId, currentProject }} /></EmbedThreadShell>;

  return (
    <div className={`app ${sidebarOpen ? 'sidebar-open' : ''} ${drawer.open ? 'drawer-open' : ''}`}>
      <button type="button" className="sidebar-toggle" aria-label="Open menu" onClick={() => setSidebarOpen((o) => !o)}>
        <MenuIcon />
      </button>

      {!onMobile ? null : (
        <TopBar onToggleSidebar={() => setSidebarOpen((o) => !o)} title={drawer.info.title || ''} actions={drawer.info.actions} />
      )}
      <div className="backdrop" onClick={() => setSidebarOpen(false)} aria-hidden="true" />

      <Sidebar
        projectId={projectId}
        open={sidebarOpen}
        onMobile={onMobile}
        openConversationId={openConversationId}
        creating={creating}
        onNewConversation={newConversation}
        closeSidebar={() => setSidebarOpen(false)}
        threadsApi={threadState}
      />

      <main className="main">
        <ScreenNavigation />
        <MentionBanner userId={user?.id} projectId={projectId} openConversationId={openConversationId} />
        <HealthBanner />
        <NoticeBanner />
        {error ? (
          <div className="error-box" role="alert">
            {error}{' '}
            <button type="button" className="link-btn" onClick={() => setError(null)}>
              dismiss
            </button>
          </div>
        ) : null}
        <Outlet context={{ projectId, currentProject }} />
      </main>

      <JabberCompanion />

      <DetailsDrawer projectName={currentProject?.name} />
    </div>
  );
}
